import ChannelSelector from "@components/ChannelSelector";
import ExportButton from "@components/ExportButton";
import { FilterBar, FilterRow } from "@components/FilterBar";
import ObjectSelect from "@components/ObjectSelect";
import PeriodFilter, { type PeriodRange } from "@components/PeriodFilter";
import { type ChannelMeterRow, useChannelsFlat, useObjects } from "@features/devices/api";
import { PageLayoutWithHelp } from "@features/page-help";
import { type MatrixCell, useMatrixReport } from "@features/reports/api";
import { buildExportUrl } from "@shared/buildExportUrl";
import { useGlobalObject } from "@shared/hooks/useGlobalObject";
import EnterpriseTable from "@shared/ui/enterprise-table";
import { useDefaultEmail } from "@shared/useDefaultEmail";
import { useMailComposer } from "@shared/useMailComposer";
import { useStandardPagination } from "@shared/useStandardPagination";
import { Alert, Checkbox, Radio, Select, Space } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";

const measurementOptions = [
  { label: "A+", value: 1 },
  { label: "A-", value: 2 },
  { label: "R+", value: 3 },
  { label: "R-", value: 4 },
];

const modeOptions = [
  { label: "Энергия", value: "energy" },
  { label: "Мощность", value: "power" },
];

const stepOptions = [
  { label: "30 мин", value: 30 },
  { label: "60 мин", value: 60 },
];

const unitOptions = [
  { label: "кВт⋅ч", value: "kw" },
  { label: "МВт⋅ч", value: "mw" },
];

interface MatrixRecord {
  key: string;
  date: string;
  total?: number | null;
  [slot: string]: string | number | null | MatrixCell | undefined;
}

const formatValue = (value?: number | null) => {
  if (value === undefined || value === null) {
    return "—";
  }
  return value.toFixed(3);
};

const ReportsMatrixPage = () => {
  const objectsQuery = useObjects();
  const objects = objectsQuery.data ?? [];
  const { selectedObject, setSelectedObject } = useGlobalObject(objects);
  const [selectedChannel, setSelectedChannel] = useState<number>();
  const [measurement, setMeasurement] = useState<number>(1);
  const [mode, setMode] = useState<string>("energy");
  const [step, setStep] = useState<number>(60);
  const [useLosses, setUseLosses] = useState<boolean>(false);
  const [unitMode, setUnitMode] = useState<"kw" | "mw">("kw");
  const [period, setPeriod] = useState<PeriodRange>(() => {
    const start = dayjs().startOf("month");
    return { from: start, to: start.add(1, "month") };
  });

  const regionID = selectedObject?.regionId;
  const objectID = selectedObject?.objectId;

  const channelsQuery = useChannelsFlat(objectID, undefined, 500);
  const channelOptions = useMemo(
    () =>
      (channelsQuery.data ?? []).map((channel: ChannelMeterRow) => ({
        label: `${channel.meter_number} — ${channel.feeder_name ?? channel.channel_label}`,
        value: channel.meter_number,
      })),
    [channelsQuery.data],
  );

  useEffect(() => {
    if (selectedChannel && channelOptions.some((option) => option.value === selectedChannel)) {
      return;
    }
    if (channelOptions.length > 0) {
      setSelectedChannel(channelOptions[0].value);
    } else {
      setSelectedChannel(undefined);
    }
  }, [channelOptions, selectedChannel]);

  const handlePeriodChange = (range: PeriodRange) => {
    if (!range.from) {
      return;
    }
    const start = range.from.startOf("month");
    setPeriod({ from: start, to: start.add(1, "month") });
  };

  const monthISO = period.from?.format("YYYY-MM-DD");

  const matrixQuery = useMatrixReport({
    region: regionID,
    object: objectID,
    channel: selectedChannel,
    date: monthISO,
    measurement,
    step,
    mode,
    useLosses,
    useMWT: unitMode === "mw",
  });

  const matrixData = matrixQuery.data;
  const intervalMinutes = matrixData?.interval_minutes ?? step;
  const rows = matrixData?.rows ?? [];
  const intervalCount = Math.max(1, Math.floor(1440 / intervalMinutes));

  const slotLabels = useMemo(() => {
    const labels: string[] = [];
    for (let i = 0; i < intervalCount; i++) {
      const start = dayjs()
        .startOf("day")
        .add(i * intervalMinutes, "minute");
      const end = start.add(intervalMinutes, "minute");
      labels.push(`${start.format("HH:mm")}-${end.format("HH:mm")}`);
    }
    return labels;
  }, [intervalCount, intervalMinutes]);

  const dataSource: MatrixRecord[] = rows.map((row) => {
    const record: MatrixRecord = {
      key: row.date,
      date: dayjs(row.date).format("DD.MM.YYYY"),
    };
    if (row.total !== undefined) {
      record.total = row.total;
    }
    row.intervals.forEach((cell, idx) => {
      record[`slot-${idx}`] = cell;
    });
    return record;
  });

  const pagination = useStandardPagination(dataSource.length);

  const columns: ColumnsType<MatrixRecord> = useMemo(() => {
    const list: ColumnsType<MatrixRecord> = [
      {
        title: "Дата",
        dataIndex: "date",
        fixed: "left",
        width: 130,
      },
      {
        title: mode === "power" ? "Максимум" : "Итого",
        dataIndex: "total",
        width: 140,
        render: (value?: number | null) => formatValue(value),
      },
    ];
    slotLabels.forEach((label, index) => {
      list.push({
        title: label,
        dataIndex: `slot-${index}`,
        width: 90,
        render: (cell?: { value?: number | null }) => formatValue(cell?.value),
      });
    });
    return list;
  }, [mode, slotLabels]);

  const ready =
    regionID !== undefined && objectID !== undefined && selectedChannel !== undefined && monthISO;

  const exportUrl = ready
    ? buildExportUrl("/api/reports/matrix", {
        region: regionID,
        object: objectID,
        channel: selectedChannel,
        date: monthISO,
        measurement,
        step,
        mode,
        use_losses: useLosses || undefined,
        use_mwt: unitMode === "mw" || undefined,
      })
    : undefined;
  const currentObject = useMemo(
    () => objects.find((obj) => obj.n_ob === objectID && obj.syb_rnk === regionID),
    [objects, objectID, regionID],
  );
  const channelLabel =
    channelOptions.find((option) => option.value === selectedChannel)?.label ??
    (selectedChannel ? `Счётчик №${selectedChannel}` : "—");
  const measurementLabel =
    measurementOptions.find((option) => option.value === measurement)?.label ?? `№${measurement}`;
  const stepLabel = stepOptions.find((option) => option.value === step)?.label ?? `${step} мин`;
  const modeLabel = modeOptions.find((option) => option.value === mode)?.label ?? mode;
  const unitLabel = unitOptions.find((option) => option.value === unitMode)?.label ?? unitMode;
  const defaultEmail = useDefaultEmail();
  const { openComposer, mailModal, sendDisabled } = useMailComposer({ defaultEmail, exportUrl });

  const handleSendMail = () => {
    if (!ready) {
      return;
    }
    const objectLabel = currentObject?.txt
      ? `${currentObject.txt} (№${currentObject.n_ob})`
      : objectID
        ? `Объект №${objectID}`
        : "Объект";
    const monthLabel = period.from ? period.from.format("MMMM YYYY") : "—";
    openComposer({
      subject: `Матричные данные — ${objectLabel}`,
      body: `Отчёт: Матричные данные\nОбъект: ${objectLabel}\nСчётчик: ${channelLabel}\nМесяц: ${monthLabel}\nПоказатель: ${measurementLabel}\nРежим: ${modeLabel}\nШаг: ${stepLabel}\nЕдиницы: ${unitLabel}\nУчитывать потери: ${useLosses ? "Да" : "Нет"}\n\nВо вложении — XLSX.`,
      filename: `matrix_${selectedChannel ?? "channel"}_${period.from?.format("YYYYMM") ?? "month"}.xlsx`,
    });
  };

  return (
    <PageLayoutWithHelp
      title="Матричные данные за период"
      extra={<ExportButton url={exportUrl} onSend={handleSendMail} sendDisabled={sendDisabled} />}
    >
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <FilterBar>
          <FilterRow>
            <ObjectSelect
              objects={objects}
              loading={objectsQuery.isLoading}
              value={selectedObject}
              onChange={(value) => setSelectedObject(value)}
              style={{ minWidth: 320, flex: 1 }}
            />
            <ChannelSelector
              options={channelOptions}
              value={selectedChannel}
              loading={channelsQuery.isLoading}
              onChange={(value) => setSelectedChannel(value)}
              style={{ minWidth: 260, flex: 1 }}
            />
            <div style={{ minWidth: 320, flex: 1 }}>
              <PeriodFilter
                value={period}
                onChange={handlePeriodChange}
                showMonthControls
                showDayControls={false}
                showRangeControls={false}
                monthLabel=""
              />
            </div>
          </FilterRow>
          <FilterRow>
            <Select
              style={{ width: 160 }}
              options={measurementOptions}
              value={measurement}
              onChange={(value) => setMeasurement(value)}
            />
            <Select
              style={{ width: 140 }}
              options={stepOptions}
              value={step}
              onChange={(value) => setStep(value)}
            />
            <Radio.Group
              options={modeOptions}
              optionType="button"
              buttonStyle="solid"
              value={mode}
              onChange={(e) => setMode(e.target.value)}
            />
            <Radio.Group
              options={unitOptions}
              optionType="button"
              buttonStyle="solid"
              value={unitMode}
              onChange={(e) => setUnitMode(e.target.value)}
            />
            <Checkbox checked={useLosses} onChange={(e) => setUseLosses(e.target.checked)}>
              Учитывать потери
            </Checkbox>
          </FilterRow>
        </FilterBar>
        {!ready && (
          <Alert
            type="info"
            showIcon
            title="Выберите объект, счётчик и месяц, чтобы построить отчёт."
            style={{ maxWidth: 600 }}
          />
        )}
        {mailModal}
        {ready && (
          <EnterpriseTable<MatrixRecord>
            rowKey="key"
            columns={columns}
            dataSource={dataSource}
            loading={matrixQuery.isLoading}
            pagination={pagination}
            scroll={{ x: 1200 }}
            size="small"
            toolbar={{ reload: true, onReload: () => matrixQuery.refetch() }}
          />
        )}
      </Space>
    </PageLayoutWithHelp>
  );
};

export default ReportsMatrixPage;
